import { AuthError } from './provider';
import type { AuthProvider, StaffUser } from './provider';

/**
 * The development sign-in: an unsigned token in the shape `core-api`'s
 * `FakeVerifier` accepts (`alg: "none"`, empty signature, `tenant_id` and `role`
 * claims), so a laptop with no Firebase user can run the whole app against a
 * local `core-api` started with `AUTH_FAKE_TOKENS=1`.
 *
 * It is a copy of `mintDevToken` from `apps/core-api/src/auth/token-verifier.ts`
 * rather than an import of it, for the reason `wire.ts` gives: apps do not
 * cross-import, and that module reaches for `Buffer`. Fifteen lines of
 * base64url is the cheaper of the two prices. If the token shape ever changes,
 * `auth.test.ts` fails here and the round-trip test in `core-api` fails there.
 *
 * **The tenant comes from build-time config, and the role is `owner`.** Both are
 * development conveniences and neither is a security decision: the real
 * credential's claims are minted by the operator and verified against Google's
 * JWKS, and this class refuses to be the thing that ships by being reachable
 * only through `VITE_AUTH_FAKE`.
 */

const STORAGE_KEY = 'yourstore.dev-session';
const LIFETIME_SECONDS = 3600;
/** Re-mint before the server would call it expired, not after. */
const REFRESH_MARGIN_SECONDS = 300;

interface DevSession {
  uid: string;
  email: string;
}

export class FakeAuthProvider implements AuthProvider {
  readonly kind = 'fake' as const;
  private readonly tenantId: string;
  private readonly storage: Storage | null;
  private readonly now: () => number;
  private readonly listeners = new Set<(user: StaffUser | null) => void>();
  private session: DevSession | null = null;

  constructor(options: { tenantId: string; storage?: Storage | null; now?: () => number }) {
    this.tenantId = options.tenantId;
    this.storage = options.storage === undefined ? safeStorage() : options.storage;
    this.now = options.now ?? Date.now;
    this.session = this.restore();
  }

  current(): StaffUser | null {
    return this.session === null ? null : { uid: this.session.uid, email: this.session.email };
  }

  async signIn(email: string, _password: string): Promise<void> {
    const trimmed = email.trim();
    if (trimmed.length === 0) {
      throw new AuthError('bad_credentials', 'An email address is required.');
    }
    this.session = { uid: `dev-${trimmed}`, email: trimmed };
    this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.session));
    this.emit();
  }

  async signOut(): Promise<void> {
    this.session = null;
    this.storage?.removeItem(STORAGE_KEY);
    this.emit();
  }

  async token(): Promise<string | null> {
    if (this.session === null) return null;
    return mintDevToken({
      uid: this.session.uid,
      email: this.session.email,
      tenantId: this.tenantId,
      expiresAt: Math.floor(this.now() / 1000) + LIFETIME_SECONDS + REFRESH_MARGIN_SECONDS,
    });
  }

  onChange(listener: (user: StaffUser | null) => void): () => void {
    this.listeners.add(listener);
    listener(this.current());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.current());
  }

  private restore(): DevSession | null {
    const raw = this.storage?.getItem(STORAGE_KEY);
    if (raw === null || raw === undefined) return null;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (
        typeof parsed === 'object' &&
        parsed !== null &&
        typeof (parsed as DevSession).uid === 'string'
      ) {
        return parsed as DevSession;
      }
    } catch {
      // A corrupt dev session is not worth a error path. Sign in again.
    }
    return null;
  }
}

export interface DevTokenOptions {
  tenantId: string;
  uid?: string;
  email?: string;
  role?: 'owner' | 'staff' | 'readonly';
  expiresAt?: number;
}

export function mintDevToken(options: DevTokenOptions): string {
  const header = base64Url(JSON.stringify({ alg: 'none', typ: 'JWT' }));
  const payload = base64Url(
    JSON.stringify({
      sub: options.uid ?? 'dev-user',
      tenant_id: options.tenantId,
      role: options.role ?? 'owner',
      ...(options.email === undefined ? {} : { email: options.email }),
      exp: options.expiresAt ?? Math.floor(Date.now() / 1000) + 3600,
    }),
  );
  return `${header}.${payload}.`;
}

function base64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function safeStorage(): Storage | null {
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}
